import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Award, Loader2, Lock, Flame, Trophy, Dumbbell, Camera, Share2, Link as LinkIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FAMILIES = ["streak", "goals", "workouts", "photos"];

const FAMILY_META = {
  streak:   { label: "Streak",   icon: Flame,    color: "#FF7A2A", subtitle: "Show up daily" },
  goals:    { label: "Goals",    icon: Trophy,   color: "#FFB020", subtitle: "Set and chase targets" },
  workouts: { label: "Workouts", icon: Dumbbell, color: "#6B9BFF", subtitle: "Volume over time" },
  photos:   { label: "Photos",   icon: Camera,   color: "#A06BFF", subtitle: "Document the journey" },
};

const SUFFIX = { streak: "d", goals: "", workouts: "", photos: "" };

const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const downloadCard = async (type, tier, label) => {
  try {
    const payload =
      type === "streak"
        ? { type: "streak", payload: { streak_days: tier, longest: tier } }
        : { type: "badge", payload: { badge_type: type, tier, label } };
    const { data } = await api.post("/share/card", payload, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `daione-${type}-${tier}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Share card downloaded");
  } catch {
    toast.error("Couldn't download the share card");
  }
};

const copyDeeplink = async (type, tier) => {
  const url = `${window.location.origin}/b/${type}/${tier}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied", { description: url });
  } catch {
    // Fallback for older browsers
    const ta = document.createElement("textarea");
    ta.value = url;
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
      toast.success("Share link copied", { description: url });
    } catch {
      toast.error("Couldn't copy — manually copy from the address bar");
    }
    ta.remove();
  }
};

const BadgeTile = ({ family, tier, label, earned, awardedAt, onShare, onCopy }) => {
  const meta = FAMILY_META[family];
  const Icon = meta.icon;
  const earnedStyle = earned
    ? { color: meta.color, borderColor: meta.color, backgroundColor: `${meta.color}1a` }
    : undefined;
  return (
    <div
      className={`relative border p-4 flex flex-col items-center justify-between text-center min-h-[140px] ${
        earned ? "" : "border-white/10 bg-[#0a0a0a] opacity-60"
      }`}
      style={earnedStyle}
      data-testid={`achievement-tile-${family}-${tier}-${earned ? "earned" : "locked"}`}
    >
      <div className="flex flex-col items-center">
        {earned ? (
          <Icon className="w-5 h-5 mb-1" style={{ color: meta.color }} />
        ) : (
          <Lock className="w-4 h-4 text-white/30 mb-1" />
        )}
        <div className={`font-display text-2xl leading-none ${earned ? "text-white" : "text-white/40"}`}>
          {tier}
          {SUFFIX[family] && (
            <span className="text-[10px] text-white/40 ml-0.5">{SUFFIX[family]}</span>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1 leading-tight">
          {label}
        </div>
      </div>
      {earned && (
        <div className="flex items-center gap-1 mt-3 w-full">
          <button
            type="button"
            onClick={onShare}
            className="flex-1 text-[10px] uppercase tracking-widest bg-white/5 hover:bg-white/15 border border-white/10 py-1 inline-flex items-center justify-center gap-1"
            data-testid={`achievement-share-${family}-${tier}`}
            title="Download share card"
          >
            <Share2 className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={onCopy}
            className="flex-1 text-[10px] uppercase tracking-widest bg-white/5 hover:bg-white/15 border border-white/10 py-1 inline-flex items-center justify-center gap-1"
            data-testid={`achievement-copy-${family}-${tier}`}
            title="Copy public share link"
          >
            <LinkIcon className="w-3 h-3" />
          </button>
        </div>
      )}
      {earned && awardedAt && (
        <div className="absolute top-2 right-2 text-[9px] uppercase tracking-widest text-white/40">
          {formatRelative(awardedAt)}
        </div>
      )}
    </div>
  );
};

export default function AchievementsPage() {
  const [earned, setEarned] = useState(null);
  const [tiers, setTiers] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [earnedRes, tiersRes] = await Promise.all([
          api.get("/badges"),
          api.get("/badges/tiers"),
        ]);
        setEarned(earnedRes.data || []);
        setTiers(tiersRes.data || {});
      } catch {
        setEarned([]);
        setTiers({});
      }
    })();
  }, []);

  const earnedByFamily = useMemo(() => {
    const map = {};
    FAMILIES.forEach((f) => (map[f] = new Map()));
    (earned || []).forEach((b) => {
      if (map[b.type]) map[b.type].set(b.tier, b);
    });
    return map;
  }, [earned]);

  const totalEarned = earned?.length || 0;
  const totalTiers = useMemo(
    () => (tiers ? FAMILIES.reduce((n, f) => n + (tiers[f]?.length || 0), 0) : 0),
    [tiers]
  );

  const recent = useMemo(() => {
    if (!earned) return [];
    return [...earned]
      .sort((a, b) => (a.awarded_at < b.awarded_at ? 1 : -1))
      .slice(0, 5);
  }, [earned]);

  if (earned === null || tiers === null) {
    return (
      <div className="p-10 text-white/40 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading achievements…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="achievements-page">
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-2">
          <Award className="w-4 h-4 text-[#6B9BFF]" />
          <div className="label-eyebrow text-[#6B9BFF]">Achievements</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none">Your milestones</h1>
        <p className="text-white/60 mt-3 max-w-2xl">
          Every streak, goal, set, and progress photo gets you closer to the next badge.
          Share any earned milestone to celebrate the work.
        </p>
        <div className="mt-4 text-xs uppercase tracking-widest text-white/40" data-testid="achievements-count">
          <span className="text-white font-bold">{totalEarned}</span> of {totalTiers} unlocked
        </div>
      </div>

      {/* Recently unlocked */}
      <section className="mb-12" data-testid="recently-unlocked-section">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#6B9BFF]" />
          <h2 className="label-eyebrow text-white">Recently unlocked</h2>
        </div>
        {recent.length === 0 ? (
          <div className="border border-white/10 bg-[#121212] p-8 text-center text-white/40 text-sm italic">
            No badges yet — knock out a 3-day checklist streak, set your first goal,
            log your first set, or upload a progress photo to get started.
          </div>
        ) : (
          <ul className="space-y-2">
            {recent.map((b) => {
              const meta = FAMILY_META[b.type];
              const Icon = meta?.icon || Award;
              return (
                <li
                  key={`${b.type}-${b.tier}`}
                  className="border border-white/10 bg-[#121212] p-4 flex items-center gap-4"
                  data-testid={`recent-badge-${b.type}-${b.tier}`}
                >
                  <Icon className="w-5 h-5 shrink-0" style={{ color: meta?.color || "#6B9BFF" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-sm">{b.label}</div>
                    <div className="text-xs text-white/40">{formatRelative(b.awarded_at)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadCard(b.type, b.tier, b.label)}
                    className="text-[10px] uppercase tracking-widest bg-white/5 hover:bg-white/15 border border-white/10 px-3 py-1.5 inline-flex items-center gap-1"
                    data-testid={`recent-share-${b.type}-${b.tier}`}
                  >
                    <Share2 className="w-3 h-3" /> Card
                  </button>
                  <button
                    type="button"
                    onClick={() => copyDeeplink(b.type, b.tier)}
                    className="text-[10px] uppercase tracking-widest bg-white/5 hover:bg-white/15 border border-white/10 px-3 py-1.5 inline-flex items-center gap-1"
                    data-testid={`recent-copy-${b.type}-${b.tier}`}
                  >
                    <LinkIcon className="w-3 h-3" /> Link
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* By family */}
      <div className="space-y-10">
        {FAMILIES.map((family) => {
          const meta = FAMILY_META[family];
          const Icon = meta.icon;
          const familyTiers = tiers[family] || [];
          const earnedMap = earnedByFamily[family];
          return (
            <section key={family} data-testid={`achievement-family-${family}`}>
              <div className="flex items-baseline justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  <h2 className="font-display text-2xl">{meta.label}</h2>
                  <span className="text-xs text-white/40 ml-2">{meta.subtitle}</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">
                  {earnedMap.size} / {familyTiers.length}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {familyTiers.map(({ tier, label }) => {
                  const badge = earnedMap.get(tier);
                  return (
                    <BadgeTile
                      key={tier}
                      family={family}
                      tier={tier}
                      label={label}
                      earned={!!badge}
                      awardedAt={badge?.awarded_at}
                      onShare={() => downloadCard(family, tier, label)}
                      onCopy={() => copyDeeplink(family, tier)}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
